'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { partnerNetworkService } from '@/lib/services/partnerNetworkService';

interface FormData {
  organizationName: string;
  organizationType: string;
  country: string;
  website: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  description: string;
  focusAreas: string[];
  partnershipGoals: string;
}

const PartnershipApplicationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    organizationType: '',
    country: '',
    website: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    focusAreas: [],
    partnershipGoals: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const organizationTypes = [
    'Government Agency',
    'NGO',
    'Faith-Based Organization',
    'University',
    'UN Agency',
    'Corporate',
    'Community Organization',
  ];

  const availableFocusAreas = [
    'Peace Education',
    'Conflict Resolution',
    'Humanitarian Aid',
    'Youth Engagement',
    'Community Development',
    'Research & Evidence',
    'Policy Advocacy',
    'Cultural Exchange',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFocusAreaToggle = (area: string) => {
    setFormData((prev) => ({
      ...prev,
      focusAreas: prev.focusAreas.includes(area)
        ? prev.focusAreas.filter((a) => a !== area)
        : [...prev.focusAreas, area],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await partnerNetworkService.submitApplication(formData);
      
      if (result) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            organizationName: '',
            organizationType: '',
            country: '',
            website: '',
            contactName: '',
            contactEmail: '',
            contactPhone: '',
            description: '',
            focusAreas: [],
            partnershipGoals: '',
          });
        }, 3000);
      } else {
        setSubmitError('Failed to submit application. Please try again.');
      }
    } catch (error: any) {
      setSubmitError('An error occurred. Please try again later.');
      console.error('Partnership application submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-success/10 border border-success rounded-lg p-8 text-center">
        <Icon name="CheckCircleIcon" size={64} className="mx-auto mb-4 text-success" />
        <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
          Application Submitted!
        </h3>
        <p className="text-foreground">
          Thank you for your interest in partnering with SIG Day. Our team will review your
          application and contact you within 5-7 business days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow-md p-6">
      <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
        Partnership Application
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Organization Name *
          </label>
          <input
            type="text"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Organization Type *
          </label>
          <select
            name="organizationType"
            value={formData.organizationType}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          >
            <option value="">Select type...</option>
            {organizationTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">Country *</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">Website *</label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Contact Name *
          </label>
          <input
            type="text"
            name="contactName"
            value={formData.contactName}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Contact Email *
          </label>
          <input
            type="email"
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-foreground mb-2">
            Contact Phone
          </label>
          <input
            type="tel"
            name="contactPhone"
            value={formData.contactPhone}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-foreground mb-2">
          Organization Description *
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
          rows={4}
          className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background resize-none"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-foreground mb-3">
          Focus Areas * (Select all that apply)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {availableFocusAreas.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() => handleFocusAreaToggle(area)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                formData.focusAreas.includes(area)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-border'
              }`}
            >
              {area}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-foreground mb-2">
          Partnership Goals *
        </label>
        <textarea
          name="partnershipGoals"
          value={formData.partnershipGoals}
          onChange={handleInputChange}
          required
          rows={4}
          placeholder="Describe what you hope to achieve through partnership with SIG Day..."
          className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background resize-none"
        />
      </div>

      {submitError && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md text-sm mb-6">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-end space-x-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </div>
    </form>
  );
};

export default PartnershipApplicationForm;